/**
 * Attachment status — the words the desk reads for what the engine did with a file.
 *
 * Every status the backend can store on an attachment (`AttachmentResult.status`
 * in backend/app/parsing/attachments.py, re-stamped by archive_intake.py) has its
 * own label here. A status missing from this map once reached a client as its raw
 * code ("parsed_freeform"); backend/tests/test_attachment_status_labels.py now
 * fails when the engine can emit a status this map does not name.
 *
 * Keep one key per line: that test reads the keys out of this file.
 */

const PARSED = 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
const CONFIRM = 'text-amber-400 bg-amber-400/10 border-amber-400/20'
const BLOCKED = 'text-red-400 bg-red-400/10 border-red-400/20'
const UNKNOWN = 'text-stone-400 bg-stone-400/10 border-stone-400/20'

export interface AttachmentStatusLabel {
  label: string
  cls: string
}

export const ATTACHMENT_STATUS_LABELS: Record<string, AttachmentStatusLabel> = {
  parsed_template: { label: 'parsed · locked template', cls: PARSED },
  // No locked template matched, so the grid was read on exact, agreeing label
  // matches only. What binds is not flagged wholesale (unlike OCR); metrics the
  // scan could not settle are held for review and counted in the detail line.
  parsed_freeform: { label: 'parsed · workbook (no template)', cls: PARSED },
  parsed_text:{ label: 'parsed · text grammar', cls: PARSED },
  parsed_ocr: { label: 'parsed · OCR — confirm values', cls: CONFIRM },
  parsed_archive: { label: 'ZIP expanded', cls: PARSED },
  needs_model_onboarding: { label: 'needs model onboarding', cls: CONFIRM },
  template_ambiguous: { label: 'template ambiguous — choose', cls: CONFIRM },
  unparsed: { label: 'human review', cls: BLOCKED },
  error: { label: 'unreadable', cls: BLOCKED },
}

/** The label for one stored status. A status this map does not know still reads
 *  as words, never as a snake_case code. */
export function attachmentStatusLabel(status: string | null | undefined): AttachmentStatusLabel {
  const code = (status ?? '').trim()
  const known = Object.prototype.hasOwnProperty.call(ATTACHMENT_STATUS_LABELS, code)
    ? ATTACHMENT_STATUS_LABELS[code]
    : undefined
  if (known) return known
  const words = code.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim()
  return { label: words || 'status not recorded', cls: UNKNOWN }
}

export default function AttachmentStatusBadge({ status }: { status: string | null | undefined }) {
  const m = attachmentStatusLabel(status)
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${m.cls}`}>
      {m.label}
    </span>
  )
}
